import {
  NOTIFICATION_TYPES,
  DRAWER_PLACEMENTS,
} from "../../../../../config/constants";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Divider,
  Drawer,
  Form,
  Input,
  InputRef,
  Space,
  Tree,
} from "antd";
import type { ColumnsType, ColumnType } from "antd/es/table";
import {
  FilterFilled,
  ArrowUpOutlined,
  ArrowDownOutlined,
  SearchOutlined,
  EditOutlined,
  CloseOutlined,
  CheckOutlined,
} from "@ant-design/icons";
import { FilterConfirmProps } from "antd/es/table/interface";
import Highlighter from "react-highlight-words";
import { DataNode, TreeProps } from "antd/lib/tree";
import ConditionService from "../../../../../services/conditions/ConditionService";
import { UpdateCondition } from "../../../../../models/migrationrules/UpdateCondition";
import { CreateNewCondition } from "../../../../../models/migrationrules/CreateNewCondition";
import Datatable from "../../../../shared/components/Datatable";
import { useTranslation } from "react-i18next";
import NotificationsUtil from "../../../../shared/utils/NotificationsUtil";
import {GetTablesRequest} from "../../../../../models/tables/GetTablesRequest";
import TablesService from "../../../../../services/tables/TablesService";


const { Search } = Input;

enum RowAction {
  View = 1,
  Edit,
  Create,
}

const RuleConditions = ({

  newConditionModalEnabled,
  setNewConditionModalEnabled,
  selectable,
  multiSelect,
  selectedRows,
  setSelectedRows,
}: {
  newConditionModalEnabled: boolean;
  setNewConditionModalEnabled: (val: boolean) => void;
  selectable: boolean;
  multiSelect: boolean;
  selectedRows: any;
  setSelectedRows: any;
}) => {
  const { t, i18n } = useTranslation("translations");

  interface DataType {
    key: string;
    id: number;
    value: string;
  }

  type DataIndex = keyof DataType;

  const [data, setData] = useState<DataType[]>([]);
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");

  const searchInput = useRef<InputRef>(null);

  const [idSearchValue, setIdSearchValue] = useState<string>("");
  const [idFilters, setIdFilters] = useState<any>([]);
  const [idCheckedKeys, setIdCheckedKeys] = useState<any>([]);
  const [idAscending, setIdAscending] = useState(false);
  const [idDescending, setIdDescending] = useState(false);

  const [valueSearchValue, setValueSearchValue] = useState<string>("");
  const [valueFilters, setValueFilters] = useState<any>([]);
  const [valueCheckedKeys, setValueCheckedKeys] = useState<any>([]);
  const [valueAscending, setValueAscending] = useState(false);
  const [valueDescending, setValueDescending] = useState(false);

  const [drawerAction, setDrawerAction] = useState<RowAction>();
  const [drawerTitle, setDrawerTitle] = useState<string>("");
  const [openDrawer, setOpenDrawer] = useState(false);
  const [valueInput, setValueInput] = useState("");
  const [editedRow, setEditedRow] = useState<any>();
  const [updateRowAllowed, setUpdateRowAllowed] = useState(false);
  const [isTableLoading, setIsTableLoading] = useState(true);
  const [loadingButton, setLoadingButton] = useState(false);

  const showDrawer = (action: RowAction) => {
    setDrawerAction(action);
    switch (action) {
      case RowAction.View:
        setDrawerTitle(
          t(
            "Page.Operation.MigrationRules.Tabs.RuleConditions.Labels.RowDetails",
            {
              lngs: i18n.languages,
              defaultValue: "Row details",
            }
          )!
        );
        break;
      case RowAction.Create:
        setDrawerTitle(
          t(
            "Page.Operation.MigrationRules.Tabs.RuleConditions.Labels.NewCondition",
            {
              lngs: i18n.languages,
              defaultValue: "New condition",
            }
          )!
        );
        break;
      case RowAction.Edit:
        setDrawerTitle(
          t(
            "Page.Operation.MigrationRules.Tabs.RuleConditions.Labels.EditCondition",
            {
              lngs: i18n.languages,
              defaultValue: "Edit condition",
            }
          )!
        );
        break;
      default:
        break;
    }
    setOpenDrawer(true);
  };

  const onClose = () => {
    setOpenDrawer(false);
    setValueInput("");
    setEditedRow(null);
    setNewConditionModalEnabled(false);
  };

  const onDoubleClickRow = (record: any) => {
    showDrawer(RowAction.View);
    setValueInput(record.value);
    setEditedRow(record);
  };

  const onEditCondition = (record: any) => {
    showDrawer(RowAction.Edit);
    setValueInput(record.value);
    setEditedRow(record);
  };

  const onCopyCondition = (record: any) => {
    showDrawer(RowAction.Create);
    setValueInput(record.value);
  };

  const onDeleteCondition = (id: number) => {
    ConditionService.deleteCondition(id)
      .then((response: any) => {
        setIsTableLoading(true);
        NotificationsUtil.openNotificationDialog({
          description: t(
            "Page.Operation.MigrationRules.Tabs.RuleConditions.Notifications.Condition.DeleteSuccessDescription",
            {
              lngs: i18n.languages,
              defaultValue: "Condition deleted successfully.",
            }
          )!,
          type: NOTIFICATION_TYPES.SUCCESS,
          logError: false,
        });
      })
      .catch((error: any) => {
        NotificationsUtil.openNotificationDialog({
          description: error.response.data,
          type: NOTIFICATION_TYPES.ERROR,
          logError: true,
        });
      });
  };

  const onSaveCondition = () => {
    setLoadingButton(true);
    if (editedRow?.id) {
      const model: UpdateCondition = {
        id: editedRow.id,
        Value: valueInput,
        SkipValidation: true,
      };
      ConditionService.updateCondition(model)
          .then((response: any) => {
            setIsTableLoading(true);
            onClose();
            setValueInput("");
            NotificationsUtil.openNotificationDialog({
              description: t(
                  "Page.Operation.MigrationRules.Tabs.RuleConditions.Notifications.Condition.UploadSuccessDescription",
                  {
                    lngs: i18n.languages,
                    defaultValue: "Condition edited successfully.",
                  }
              )!,
              type: NOTIFICATION_TYPES.SUCCESS,
              logError: false,
            });
            setLoadingButton(false);
            ConditionService.getAll().then((response: any) => {
              const newData = response.data.map((x: any) => ({
                ...x,
                key: x.id,
              }));

              if (newConditionModalEnabled) {
                setSelectedRows([newData.sort((a, b) => b.id - a.id)[0]]);
                setNewConditionModalEnabled(false);
                setData(newData.sort((a, b) => b.id - a.id));
                setIsTableLoading(false);
              } else {
                setData(newData);
                setIsTableLoading(false);
              }
            });
          })
          .catch((error: any) => {
            NotificationsUtil.openNotificationDialog({
              description: error.response.data,
              type: NOTIFICATION_TYPES.ERROR,
              logError: true,
            });
          });
    } else {
      const model: CreateNewCondition = {
        Value: valueInput,
        SkipValidation: true,
      };
      ConditionService.createCondition(model)
          .then((response: any) => {
            setIsTableLoading(true);
            onClose();
            setValueInput("");
            NotificationsUtil.openNotificationDialog({
              description: t(
                  "Page.Operation.MigrationRules.Tabs.RuleConditions.Notifications.Condition.CreateSuccessDescription",
                  {
                    lngs: i18n.languages,
                    defaultValue: "Condition created successfully.",
                  }
              )!,
              type: NOTIFICATION_TYPES.SUCCESS,
              logError: false,
            });
            setLoadingButton(false);
            ConditionService.getAll().then((response: any) => {
              const newData = response.data.map((x: any) => ({
                ...x,
                key: x.id,
              }));

              if (newConditionModalEnabled) {
                setSelectedRows([newData.sort((a, b) => b.id - a.id)[0]]);
                setNewConditionModalEnabled(false);
                setData(newData.sort((a, b) => b.id - a.id));
                setIsTableLoading(false);
              } else {
                setData(newData);
                setIsTableLoading(false);
              }
            });
          })
          .catch((error: any) => {
            NotificationsUtil.openNotificationDialog({
              description: error.response.data,
              type: NOTIFICATION_TYPES.ERROR,
              logError: true,
            });
          });
    }
  };

  const onValueInputChange: React.ChangeEventHandler<HTMLTextAreaElement> = (
    event
  ) => {
    setValueInput(event.target.value);
  };

  useEffect(() => {
    if (isTableLoading) {
      ConditionService.getAll().then((response: any) => {
        const newData = response.data.map((x: any) => ({
          ...x,
          key: x.id,
        }));
        setData(newData.sort((a, b) => b.id - a.id));
        setIsTableLoading(false);
      }).catch((error: any) => {
        NotificationsUtil.openNotificationDialog({
          description: error.response.data,
          type: NOTIFICATION_TYPES.ERROR,
          logError: true,
        });
      });
    }
  }, [isTableLoading]);

  useEffect(() => {
    if (data) {
      const distinctBy = (prop: any, arr: any) => [
        ...new Set(arr.map((o: any) => o[prop])),
      ];

      let result = distinctBy("id", data);
      setIdFilters(
        result.map((value: any, index: any) => ({ key: index, title: value }))
      );

      result = distinctBy("value", data);
      setValueFilters(
        result.map((value: any, index: any) => ({ key: index, title: value }))
      );
    }
  }, [data]);

  useEffect(() => {
    if (valueInput && editedRow) {
      const def = data.find((x: any) => x.id === editedRow.id);

      if (valueInput !== def?.value) {
        setUpdateRowAllowed(true);
      } else {
        setUpdateRowAllowed(false);
      }
    } else {
      setUpdateRowAllowed(false);
    }
  }, [valueInput, editedRow]);

  useEffect(() => {
    if (newConditionModalEnabled) {
      showDrawer(RowAction.Create);
    }
  }, [newConditionModalEnabled]);

  const handleSearch = (
    selectedKeys: string[],
    confirm: (param?: FilterConfirmProps) => void,
    dataIndex: DataIndex
  ) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters: () => void) => {
    clearFilters();
    setSearchText("");
  };

  const onIdFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setIdSearchValue(value);
  };

  const onValueFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setValueSearchValue(value);
  };

  const idFilterTreeData = useMemo(() => {
    const loop = (idFilters: DataNode[]): DataNode[] =>
      idFilters
        .map((item: any) => {
          const strTitle = item.title ? item.title.toString() : "";
          const index = strTitle.indexOf(idSearchValue);
          const beforeStr = strTitle.substring(0, index);
          const afterStr = strTitle.slice(index + idSearchValue.length);
          const title =
            index > -1 ? (
              <span>
                {beforeStr}
                <span className="site-tree-search-value">{idSearchValue}</span>
                {afterStr}
              </span>
            ) : (
              <span>{strTitle}</span>
            );

          return {
            title,
            key: item.key,
            shouldRemove: index === -1,
          };
        })
        .filter((x: any) => !x.shouldRemove);

    return loop(idFilters.map((x: any) => ({ key: x.key, title: x.title })));
  }, [idFilters, idSearchValue]);

  const valueFilterTreeData = useMemo(() => {
    const loop = (valueFilters: DataNode[]): DataNode[] =>
      valueFilters
        .map((item: any) => {
          const strTitle = item.title ? (item.title as string) : "";
          const index = strTitle.indexOf(valueSearchValue);
          const beforeStr = strTitle.substring(0, index);
          const afterStr = strTitle.slice(index + valueSearchValue.length);
          const title =
            index > -1 ? (
              <span>
                {beforeStr}
                <span className="site-tree-search-value">
                  {valueSearchValue}
                </span>
                {afterStr}
              </span>
            ) : (
              <span>{strTitle}</span>
            );

          return {
            title,
            key: item.key,
            shouldRemove: index === -1,
          };
        })
        .filter((x: any) => !x.shouldRemove);

    return loop(valueFilters.map((x: any) => ({ key: x.key, title: x.title })));
  }, [valueFilters, valueSearchValue]);

  useEffect(() => {
    if (data) {
      let newTableItems = data.map((x: any) => {
        return { ...x };
      });
      if (idAscending) {
        setData([...newTableItems.sort((a, b) => (a.id > b.id ? 1 : -1))]);
      }
      if (idDescending) {
        setData([...newTableItems.sort((a, b) => (a.id < b.id ? 1 : -1))]);
      }
    }
  }, [idAscending, idDescending]);

  useEffect(() => {
    if (data) {
      let newTableItems = data.map((x: any) => {
        return { ...x };
      });
      if (valueAscending) {
        setData([
          ...newTableItems.sort((a, b) => (a.value > b.value ? 1 : -1)),
        ]);
      }
      if (valueDescending) {
        setData([
          ...newTableItems.sort((a, b) => (a.value < b.value ? 1 : -1)),
        ]);
      }
    }
  }, [valueAscending, valueDescending]);

  const onIdTreeItemCheck: TreeProps["onCheck"] = (keys, info) => {
    setIdCheckedKeys(keys);
  };
  const onValueTreeItemCheck: TreeProps["onCheck"] = (keys, info) => {
    setValueCheckedKeys(keys);
  };

  const getColumnSearchProps = (
    dataIndex: DataIndex,
    filterChangeFunc: any,
    data: any,
    checkedKeys: any,
    onTreeItemCheck: any,
    itemsAcending: any,
    itemsDescending: any,
    setItemsAscending: any,
    setItemsDescending: any
  ): ColumnType<DataType> => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div
        className="tableFilter"
        style={{ padding: "4px 10px", maxWidth: "220px" }}
      >
        <div className="sortingContainer">
          <span>
            {t("General.Labels.Sort", {
              lngs: i18n.languages,
              defaultValue: "Sort",
            })}
          </span>
          <Button
            type="text"
            icon={<ArrowUpOutlined />}
            style={{
              textAlign: "left",
              paddingLeft: "8px",
              backgroundColor: itemsAcending ? "#7BBDB1" : "",
              color: itemsAcending ? "#fff" : "",
            }}
            onClick={() => {
              setItemsAscending(!itemsAcending);
              setItemsDescending(false);
            }}
          >
            {t("General.Labels.Ascending", {
              lngs: i18n.languages,
              defaultValue: "Ascending",
            })}
          </Button>
          <Button
            type="text"
            icon={<ArrowDownOutlined />}
            style={{
              textAlign: "left",
              paddingLeft: "8px",
              backgroundColor: itemsDescending ? "#7BBDB1" : "",
              color: itemsDescending ? "#fff" : "",
            }}
            onClick={() => {
              setItemsDescending(!itemsDescending);
              setItemsAscending(false);
            }}
          >
            {t("General.Labels.Descending", {
              lngs: i18n.languages,
              defaultValue: "Descending",
            })}
          </Button>
        </div>
        <Divider />
        <Search
          prefix={<SearchOutlined />}
          allowClear
          ref={searchInput}
          placeholder={
            t("General.Placeholders.EnterValue", {
              lngs: i18n.languages,
              defaultValue: "Enter Value",
            })!
          }
          value={selectedKeys[0]}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            filterChangeFunc(e);
            setSelectedKeys(e.target.value ? [e.target.value] : []);
          }}
          onPressEnter={() =>
            handleSearch(selectedKeys as string[], confirm, dataIndex)
          }
          style={{ marginBottom: 8, display: "flex" }}
        />
        <Tree
          checkable
          onCheck={onTreeItemCheck}
          checkedKeys={checkedKeys}
          treeData={data}
        />
        <Divider />
        <div className="filterButtons">
          <Button
            onClick={() => clearFilters && handleReset(clearFilters)}
            size="small"
            type="text"
          >
            {t("General.Labels.Reset", {
              lngs: i18n.languages,
              defaultValue: "Reset",
            })}
          </Button>
          <Button
            type="primary"
            onClick={() =>
              handleSearch(selectedKeys as string[], confirm, dataIndex)
            }
            size="small"
          >
            {t("General.Labels.Ok", {
              lngs: i18n.languages,
              defaultValue: "OK",
            })}
          </Button>
        </div>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <FilterFilled style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        .toString()
        .toLowerCase()
        .includes((value as string).toLowerCase()),
    onFilterDropdownVisibleChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  const columns: ColumnsType<DataType> = [
    {
      title: t(
        "Page.Operation.MigrationRules.Tabs.RuleConditions.Table.Headers.ID",
        {
          lngs: i18n.languages,
          defaultValue: "ID",
        }
      ),
      dataIndex: "id",
      key: "id",
      width: "100px",
      filters: [],
      filterMode: "tree",
      filterSearch: true,
      ...getColumnSearchProps(
        "id",
        onIdFilterChange,
        idFilterTreeData,
        idCheckedKeys,
        onIdTreeItemCheck,
        idAscending,
        idDescending,
        setIdAscending,
        setIdDescending
      ),
      sorter: (a, b) => a.id > b.id ? 1 : -1,
    },
    {
      title: t(
        "Page.Operation.MigrationRules.Tabs.RuleConditions.Table.Headers.Value",
        {
          lngs: i18n.languages,
          defaultValue: "Value",
        }
      ),
      dataIndex: "value",
      key: "value",
      filters: [],
      filterMode: "tree",
      filterSearch: true,
      ...getColumnSearchProps(
        "value",
        onValueFilterChange,
        valueFilterTreeData,
        valueCheckedKeys,
        onValueTreeItemCheck,
        valueAscending,
        valueDescending,
        setValueAscending,
        setValueDescending
      ),
      sorter: (a, b) => a.value ? a.value.localeCompare(b.value ?? "") : "".localeCompare(b.value ?? ""),
    },
  ];

  const scroll = { y: selectable ? "calc(-270px + 91vh)" : "calc(100vh - 330px)", x: "max-content" };
  // const scroll={ y: '400px', x: 200 }
  // const scroll={undefined}

  return (
    <>
      <Datatable
        scroll={scroll}
        tableColumns={columns}
        data={data}
        setData={setData}
        isTableLoading={isTableLoading}
        otherActions={[]}
        otherRowEnabled={false}
        editRowEnabled={true}
        copyRowEnabled={true}
        deleteRowEnabled={true}
        onEditFunc={onEditCondition}
        onCopyFunc={onCopyCondition}
        onDeleteFunc={onDeleteCondition}
        onDoubleClickRow={onDoubleClickRow}
        selectable={selectable}
        multiSelect={multiSelect}
        selectedRows={selectedRows}
        setSelectedRows={setSelectedRows}
        checkPermissions={true}
        paginationDisabled={true}
      ></Datatable>
      <Drawer
        width={"400px"}
        getContainer={false}
        style={{ position: "absolute" }}
        title={drawerTitle}
        placement={DRAWER_PLACEMENTS.BOTTOM}
        onClose={onClose}
        visible={openDrawer}
        closable={false}
        extra={
          <Space>
            {RowAction?.View === drawerAction && (
              <>
                <Button
                  onClick={() => showDrawer(RowAction.Edit)}
                  icon={<EditOutlined />}
                  className={"active-btn"}
                  size="small"
                >
                  {t("General.Labels.Edit", {
                    lngs: i18n.languages,
                    defaultValue: "Edit",
                  })}
                </Button>
                <Button
                  type="ghost"
                  onClick={onClose}
                  icon={<CloseOutlined />}
                  size="small"
                />
              </>
            )}
            {RowAction?.Create === drawerAction && (
              <>
                <Button
                  disabled={!valueInput}
                  className={!valueInput ? "" : "active-btn"}
                  onClick={onSaveCondition}
                  icon={<CheckOutlined />}
                  size="small"
                  loading={loadingButton}
                >
                  {t("General.Labels.Save", {
                    lngs: i18n.languages,
                    defaultValue: "Save",
                  })}
                </Button>
                <Button
                  type="ghost"
                  onClick={onClose}
                  icon={<CloseOutlined />}
                  size="small"
                />
              </>
            )}
            {RowAction?.Edit === drawerAction && (
              <>
                <Button
                  disabled={!updateRowAllowed}
                  className={!updateRowAllowed ? "" : "active-btn"}
                  onClick={onSaveCondition}
                  icon={<CheckOutlined />}
                  size="small"
                  loading={loadingButton}
                >
                  {t("General.Labels.Update", {
                    lngs: i18n.languages,
                    defaultValue: "Update",
                  })}
                </Button>
                <Button
                  type="ghost"
                  onClick={onClose}
                  icon={<CloseOutlined />}
                  size="small"
                />
              </>
            )}
          </Space>
        }
      >
        <Form
          disabled={RowAction.View === drawerAction ? true : false}
          layout="vertical"
        >
          <Form.Item
            label={t(
              "Page.Operation.MigrationRules.Tabs.RuleConditions.Labels.Value",
              {
                lngs: i18n.languages,
                defaultValue: "Value",
              }
            )}
            tooltip={t(
              "Page.Operation.MigrationRules.Tabs.RuleConditions.Tooltips.Value",
              {
                lngs: i18n.languages,
                defaultValue: "",
              }
            )}
          >
            <Input.TextArea
              allowClear
              rows={4}
              value={valueInput}
              placeholder={
                t("General.Placeholders.EnterValue", {
                  lngs: i18n.languages,
                  defaultValue: "Enter Value",
                })!
              }
              onChange={onValueInputChange}
            ></Input.TextArea>
          </Form.Item>
        </Form>
      </Drawer>
    </>
  );
};

export default RuleConditions;
